import { useState, useEffect } from "react";
import api from "@/lib/api";
import { 
  Plus, 
  TrendingUp, 
  TrendingDown, 
  DollarSign,
  Target,
  X,
  Check,
  Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function TradeTracker() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);
  
  // Form state
  const [newTrade, setNewTrade] = useState({
    symbol: "",
    trade_type: "BUY",
    shares: "",
    entry_price: "",
    entry_date: new Date().toISOString().split('T')[0],
    suggestion_source: "AI",
    notes: ""
  });
  
  const [closeData, setCloseData] = useState({
    exit_price: "",
    exit_date: new Date().toISOString().split('T')[0],
    notes: ""
  });

  const fetchData = async () => {
    try {
      const response = await api.get("/trade-tracker/summary");
      setData(response.data);
    } catch (error) {
      console.error("Error fetching trade tracker:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddTrade = async () => {
    if (!newTrade.symbol || !newTrade.shares || !newTrade.entry_price) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    try {
      await api.post("/trade-tracker/add", {
        ...newTrade,
        shares: parseFloat(newTrade.shares),
        entry_price: parseFloat(newTrade.entry_price)
      });
      toast.success(`Added ${newTrade.trade_type} trade for ${newTrade.symbol}`);
      setShowAddDialog(false);
      setNewTrade({
        symbol: "",
        trade_type: "BUY",
        shares: "",
        entry_price: "",
        entry_date: new Date().toISOString().split('T')[0],
        suggestion_source: "AI",
        notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to add trade");
    }
  };

  const handleCloseTrade = async () => {
    if (!closeData.exit_price) {
      toast.error("Please enter exit price");
      return;
    }
    
    try {
      const response = await api.post(`/trade-tracker/${selectedTrade.id}/close`, {
        ...closeData,
        exit_price: parseFloat(closeData.exit_price)
      });
      toast.success(response.data.message);
      setShowCloseDialog(false);
      setSelectedTrade(null);
      setCloseData({
        exit_price: "",
        exit_date: new Date().toISOString().split('T')[0],
        notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to close trade");
    }
  };

  const handleDeleteTrade = async (tradeId) => {
    if (!confirm("Are you sure you want to delete this trade?")) return;
    
    try {
      await api.delete(`/trade-tracker/${tradeId}`);
      toast.success("Trade deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete trade");
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-32 bg-gray-800 rounded-lg" />
        <div className="h-48 bg-gray-800 rounded-lg" />
      </div>
    );
  }

  const summary = data?.summary || {};

  return (
    <div className="space-y-4" data-testid="trade-tracker">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-gradient-to-br from-green-900/50 to-gray-900 border-green-700/50">
          <CardContent className="p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs text-gray-400 uppercase">Total P&L</p>
            <p className={`text-lg sm:text-2xl font-bold ${summary.total_profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              ${summary.total_profit_loss?.toLocaleString() || '0'}
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-900/50 to-gray-900 border-blue-700/50">
          <CardContent className="p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs text-gray-400 uppercase">Buy Profits</p>
            <p className="text-lg sm:text-2xl font-bold text-blue-400">
              ${summary.buy_profit?.toLocaleString() || '0'}
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-900/50 to-gray-900 border-purple-700/50">
          <CardContent className="p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs text-gray-400 uppercase">Sell Savings</p>
            <p className="text-lg sm:text-2xl font-bold text-purple-400">
              ${summary.sell_savings?.toLocaleString() || '0'}
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-yellow-900/50 to-gray-900 border-yellow-700/50">
          <CardContent className="p-3 sm:p-4">
            <p className="text-[10px] sm:text-xs text-gray-400 uppercase">Win Rate</p>
            <p className="text-lg sm:text-2xl font-bold text-yellow-400">
              {summary.win_rate || 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Add Trade Button */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogTrigger asChild>
          <Button className="w-full bg-purple-600 hover:bg-purple-700" data-testid="add-trade-btn">
            <Plus className="w-4 h-4 mr-2" />
            Log New Trade (Based on AI Suggestion)
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Add Trade Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-400">Symbol</Label>
                <Input
                  placeholder="AAPL"
                  value={newTrade.symbol}
                  onChange={(e) => setNewTrade({...newTrade, symbol: e.target.value.toUpperCase()})}
                  className="bg-gray-800 border-gray-700"
                />
              </div>
              <div>
                <Label className="text-gray-400">Type</Label>
                <Select value={newTrade.trade_type} onValueChange={(v) => setNewTrade({...newTrade, trade_type: v})}>
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BUY">BUY</SelectItem>
                    <SelectItem value="SELL">SELL (Short)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-400">Shares</Label>
                <Input
                  type="number"
                  placeholder="100"
                  value={newTrade.shares}
                  onChange={(e) => setNewTrade({...newTrade, shares: e.target.value})}
                  className="bg-gray-800 border-gray-700"
                />
              </div>
              <div>
                <Label className="text-gray-400">Entry Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="150.00"
                  value={newTrade.entry_price}
                  onChange={(e) => setNewTrade({...newTrade, entry_price: e.target.value})}
                  className="bg-gray-800 border-gray-700"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-400">Date</Label>
                <Input
                  type="date"
                  value={newTrade.entry_date}
                  onChange={(e) => setNewTrade({...newTrade, entry_date: e.target.value})}
                  className="bg-gray-800 border-gray-700"
                />
              </div>
              <div>
                <Label className="text-gray-400">Source</Label>
                <Select value={newTrade.suggestion_source} onValueChange={(v) => setNewTrade({...newTrade, suggestion_source: v})}>
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AI">AI Suggestion</SelectItem>
                    <SelectItem value="SMART_MONEY">Smart Money</SelectItem>
                    <SelectItem value="POLITICIAN">Politician Trade</SelectItem>
                    <SelectItem value="INSIDER">Insider Trade</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className="text-gray-400">Notes (optional)</Label>
              <Input
                placeholder="Why I took this trade..."
                value={newTrade.notes}
                onChange={(e) => setNewTrade({...newTrade, notes: e.target.value})}
                className="bg-gray-800 border-gray-700"
              />
            </div>
            
            <Button onClick={handleAddTrade} className="w-full bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Trade
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Close Trade Dialog */}
      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Close Trade - {selectedTrade?.symbol}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-gray-800 p-3 rounded-lg">
              <p className="text-sm text-gray-400">Entry: ${selectedTrade?.entry_price} × {selectedTrade?.shares} shares</p>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-400">Exit Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="155.00"
                  value={closeData.exit_price}
                  onChange={(e) => setCloseData({...closeData, exit_price: e.target.value})}
                  className="bg-gray-800 border-gray-700"
                />
              </div>
              <div>
                <Label className="text-gray-400">Exit Date</Label>
                <Input
                  type="date"
                  value={closeData.exit_date}
                  onChange={(e) => setCloseData({...closeData, exit_date: e.target.value})}
                  className="bg-gray-800 border-gray-700"
                />
              </div>
            </div>
            
            <Button onClick={handleCloseTrade} className="w-full bg-blue-600 hover:bg-blue-700">
              <Check className="w-4 h-4 mr-2" />
              Close Trade
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Open Trades */}
      {data?.open_trades?.length > 0 && (
        <Card className="bg-gray-900/50 border-gray-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm sm:text-base text-white flex items-center gap-2">
              <Target className="w-4 h-4 text-yellow-500" />
              Open Trades ({data.open_trades.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.open_trades.map((trade) => (
              <div 
                key={trade.id} 
                className="flex items-center justify-between p-3 bg-gray-800 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Badge className={trade.trade_type === "BUY" ? "bg-green-600" : "bg-red-600"}>
                    {trade.trade_type}
                  </Badge>
                  <div>
                    <p className="font-bold text-white">{trade.symbol}</p>
                    <p className="text-xs text-gray-400">
                      {trade.shares} @ ${trade.entry_price}
                    </p>
                  </div>
                </div>
                <div className="text-right flex items-center gap-2">
                  {trade.unrealized_pnl !== null && (
                    <div>
                      <p className={`font-bold ${trade.unrealized_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        ${trade.unrealized_pnl?.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">
                        {trade.unrealized_pct >= 0 ? '+' : ''}{trade.unrealized_pct}%
                      </p>
                    </div>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedTrade(trade);
                      setShowCloseDialog(true);
                    }}
                    className="border-blue-600 text-blue-400 hover:bg-blue-600/20"
                  >
                    Close
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeleteTrade(trade.id)}
                    className="text-red-400 hover:bg-red-600/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Recent Closed Trades */}
      {data?.recent_closed?.length > 0 && (
        <Card className="bg-gray-900/50 border-gray-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm sm:text-base text-white flex items-center gap-2">
              <Check className="w-4 h-4 text-green-500" />
              Recent Closed Trades
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.recent_closed.map((trade) => (
              <div 
                key={trade.id} 
                className="flex items-center justify-between p-3 bg-gray-800 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Badge className={trade.trade_type === "BUY" ? "bg-green-600/50" : "bg-red-600/50"}>
                    {trade.trade_type}
                  </Badge>
                  <div>
                    <p className="font-bold text-white">{trade.symbol}</p>
                    <p className="text-xs text-gray-400">
                      ${trade.entry_price} → ${trade.exit_price}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-bold ${trade.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {trade.profit_loss >= 0 ? '+' : ''}${trade.profit_loss?.toLocaleString()}
                  </p>
                  <p className={`text-xs ${trade.profit_loss_pct >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {trade.profit_loss_pct >= 0 ? '+' : ''}{trade.profit_loss_pct}%
                  </p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {(!data?.open_trades?.length && !data?.recent_closed?.length) && (
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-8 text-center">
            <Target className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No trades logged yet</p>
            <p className="text-sm text-gray-500 mt-1">
              Start tracking your trades based on AI suggestions to measure performance
            </p>
          </CardContent>
        </Card>
      )}

      {/* Performance by Source */}
      {summary.by_source && Object.keys(summary.by_source).some(k => summary.by_source[k].total > 0) && (
        <Card className="bg-gray-900/50 border-gray-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm sm:text-base text-white">Performance by Source</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {Object.entries(summary.by_source).map(([source, stats]) => (
                stats.total > 0 && (
                  <div key={source} className="p-3 bg-gray-800 rounded-lg text-center">
                    <p className="text-xs text-gray-400">{source}</p>
                    <p className={`text-lg font-bold ${stats.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      ${stats.profit?.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">{stats.win_rate}% win</p>
                  </div>
                )
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
